/**
 * Enterprise-grade Mermaid diagram syntax validator and sanitizer.
 *
 * ARCHITECTURAL PRINCIPLE: MINIMAL INTERVENTION
 * - Only fix patterns that production debugging confirmed break parsing
 * - Preserve ALL Mermaid structural syntax ([*], -->, stateDiagram-v2, etc.)
 * - Use surgical precision to avoid any collateral damage
 *
 * ZERO TECHNICAL DEBT: Clean, maintainable, precisely targeted solutions.
 */

import type { Logger } from './Logger.js';

/**
 * Validation result for diagram syntax
 */
export interface ValidationResult {
  readonly isValid: boolean;
  readonly sanitizedCode: string;
  readonly errors: string[];
  readonly warnings: string[];
}

/**
 * Minimal-intervention diagram syntax validator.
 * Only addresses specific patterns confirmed to break in production.
 */
export class DiagramSyntaxValidator {
  private readonly logger: Logger;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  /**
   * Validates and sanitizes Mermaid diagram syntax with MINIMAL intervention
   *
   * @param diagramCode - Raw diagram code from HTML
   * @returns Validation result with sanitized code
   */
  validateAndSanitize(diagramCode: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    this.logger.debug('Starting minimal-intervention diagram validation', {
      originalLength: diagramCode.length,
    });

    try {
      // Step 1: Decode HTML entities only
      let sanitized = this.decodeHTMLEntities(diagramCode);

      // Step 2: Apply ONLY specific fixes for confirmed production issues
      sanitized = this.applyMinimalFixes(sanitized);

      // Step 3: Basic validation
      const syntaxValid = this.validateBasicStructure(sanitized, errors, warnings);

      // Step 4: Minimal whitespace cleanup
      sanitized = this.normalizeWhitespace(sanitized);

      this.logger.debug('Minimal validation complete', {
        originalLength: diagramCode.length,
        sanitizedLength: sanitized.length,
        isValid: syntaxValid,
        errorCount: errors.length,
        warningCount: warnings.length,
      });

      return {
        isValid: syntaxValid && errors.length === 0,
        sanitizedCode: sanitized,
        errors,
        warnings,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown validation error';
      errors.push(`Validation failed: ${errorMessage}`);

      return {
        isValid: false,
        sanitizedCode: diagramCode,
        errors,
        warnings,
      };
    }
  }

  /**
   * Decode HTML entities only (no other transformations)
   */
  private decodeHTMLEntities(text: string): string {
    return (
      text
        // Standard HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&nbsp;/g, ' ')
        // Hexadecimal entities
        .replace(/&#[xX]([0-9A-Fa-f]+);/g, (_, hex) => {
          const code = parseInt(hex, 16);
          return this.isValidCodePoint(code) ? String.fromCharCode(code) : _;
        })
        // Decimal entities
        .replace(/&#([0-9]+);/g, (_, dec) => {
          const code = parseInt(dec, 10);
          return this.isValidCodePoint(code) ? String.fromCharCode(code) : _;
        })
        // Fix known corruption patterns
        .replace(/&ﬂ°x3C¶ß/g, '<')
        .replace(/&ﬂ°x3E¶ß/g, '>')
        .replace(/&ﬂ°x7C¶ß/g, '|')
        .replace(/&ﬂ°x26¶ß/g, '&')
    );
  }

  /**
   * Apply ONLY the specific fixes confirmed by production debugging
   *
   * CRITICAL: No broad pattern matching. Only exact, known problematic cases.
   */
  private applyMinimalFixes(code: string): string {
    let sanitized = code;

    // Fix 1: ONLY currency symbols in state diagram notes (confirmed breaking)
    if (/(stateDiagram|state)/i.test(code) && /note\s+right\s+of\s+\w+:\s*State:/i.test(code)) {
      sanitized = this.fixStateNoteCurrency(sanitized);
    }

    // Fix 2: ONLY broken UML inheritance arrows (confirmed corruption patterns)
    if (/<\|[;\-—–]/.test(code)) {
      sanitized = this.fixBrokenUMLArrows(sanitized);
    }

    // Fix 3: ONLY Big-O notation in flowchart nodes (confirmed breaking)
    if (/(flowchart|graph)/i.test(code) && /O\([^)]*n[^)]*\)/.test(code)) {
      sanitized = this.fixFlowchartBigONotation(sanitized);
    }

    // Fix 4: ONLY parentheses in state transitions (confirmed breaking)
    if (/(stateDiagram|state)/i.test(code) && /-->\s*[^:\n]*\([^)]*\)\s*:/.test(code)) {
      sanitized = this.fixStateTransitionParentheses(sanitized);
    }

    // Fix 5: ONLY the exact "Exponential Time O(2^n)" pattern if it exists
    if (/Exponential\s+Time[^\]]*O\([^)]*2\^[^)]*\)/.test(code)) {
      sanitized = this.fixSpecificBigOPattern(sanitized);
    }

    return sanitized;
  }

  /**
   * Fix ONLY currency symbols in state diagram notes
   * Target: "note right of S0: State: 0¢" → "note right of S0: State: Empty"
   */
  private fixStateNoteCurrency(code: string): string {
    this.logger.debug('Applying currency fixes in state diagram notes');

    // CRITICAL FIX: Remove \b word boundaries after currency symbols
    // Currency symbols don't work with word boundaries properly
    return (
      code
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)0¢/g, '$1Empty')
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)25¢/g, '$1Q1')
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)50¢/g, '$1Half')
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)75¢/g, '$1Q3')
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)\$1(\s*\([^)]*\))?/g, '$1Full$2')
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*)(\d+)¢/g, '$1$2c')
        // Additional safety: catch any remaining currency symbols in notes
        .replace(/(note\s+right\s+of\s+\w+:\s*State:\s*[^¢$€£¥]*)[¢$€£¥]+([^¢$€£¥]*)/g, '$1$2')
    );
  }

  /**
   * Fix ONLY broken UML inheritance arrows
   */
  private fixBrokenUMLArrows(code: string): string {
    this.logger.debug('Fixing broken UML inheritance arrows');

    return code
      .replace(/<\|;\|--/g, '<|--')
      .replace(/<\|;(?!\|)/g, '<|')
      .replace(/<;/g, '<|')
      .replace(/<\|-(?!-)/g, '<|--')
      .replace(/<\|[—–]/g, '<|--');
  }

  /**
   * Fix ONLY Big-O notation in flowchart nodes (confirmed breaking)
   * Target: A[O(n)] → A[O_n]
   */
  private fixFlowchartBigONotation(code: string): string {
    this.logger.debug('Fixing Big-O notation in flowchart nodes');

    return (
      code
        // Fix O(n) patterns in node labels
        .replace(
          /(\w+\[)([^\]]*O\([^)]*n[^)]*\)[^\]]*)\]/g,
          (_, nodePrefix: string, content: string) => {
            const fixed = content.replace(/O\(([^)]*n[^)]*)\)/g, 'O_$1');
            this.logger.info(`Fixed flowchart Big-O: "${content}" → "${fixed}"`);
            return `${nodePrefix}${fixed}]`;
          }
        )
        // Fix O(n) patterns in node descriptions
        .replace(
          /(\w+\()([^)]*O\([^)]*n[^)]*\)[^)]*)\)/g,
          (_, nodePrefix: string, content: string) => {
            const fixed = content.replace(/O\(([^)]*n[^)]*)\)/g, 'O_$1');
            return `${nodePrefix}${fixed})`;
          }
        )
    );
  }

  /**
   * Fix ONLY parentheses in state transitions (confirmed breaking)
   * Target: S1 --> S2(data): action → S1 --> S2_data: action
   */
  private fixStateTransitionParentheses(code: string): string {
    this.logger.debug('Fixing parentheses in state transitions');

    return (
      code
        // Fix parentheses in state names within transitions
        .replace(/(-->\s*)(\w+)\(([^)]*)\)(\s*:)/g, '$1$2_$3$4')
        // Fix parentheses in transition labels
        .replace(
          /(\w+\s*-->\s*\w+\s*:\s*[^(\n]*)(\([^)]*\))/g,
          (_, prefix: string, parens: string) => {
            const fixed = parens.replace(/[()]/g, '_');
            return `${prefix}${fixed}`;
          }
        )
    );
  }

  /**
   * Fix ONLY the specific "Exponential Time O(2^n)" pattern
   * Target: D[Exponential Time O(2^n) possible!] → D[Exponential Time O_2_to_n possible_excl_]
   */
  private fixSpecificBigOPattern(code: string): string {
    this.logger.debug('Fixing specific Big-O pattern in Exponential Time labels');

    return code.replace(
      /\[([^\]]*Exponential\s+Time[^\]]*O\([^)]*2\^[^)]*\)[^\]]*)\]/g,
      (_, label: string) => {
        const fixed = label
          .replace(/O\(([^)]*2\^[^)]*)\)/g, (_: string, inner: string) => {
            const safeInner = inner.replace(/(\w+)\^(\w+)/g, '$1_to_$2');
            return `O_${safeInner}`;
          })
          .replace(/!/g, '_excl_');

        this.logger.info(`Fixed Big-O pattern: "${label}" → "${fixed}"`);
        return `[${fixed}]`;
      }
    );
  }

  /**
   * Basic structure validation (no complex transformations)
   */
  private validateBasicStructure(code: string, errors: string[], warnings: string[]): boolean {
    let isValid = true;

    // Check not empty
    if (!code.trim()) {
      errors.push('Empty diagram code');
      isValid = false;
    }

    // Check for balanced quotes (warning only)
    const singleQuotes = (code.match(/'/g) || []).length;
    const doubleQuotes = (code.match(/"/g) || []).length;

    if (singleQuotes % 2 !== 0) {
      warnings.push('Unbalanced single quotes detected');
    }

    if (doubleQuotes % 2 !== 0) {
      warnings.push('Unbalanced double quotes detected');
    }

    // Check for remaining problematic patterns (warnings only)
    if (/note\s+right\s+of\s+\w+:\s*[^;\n]*[¢$€£¥]/.test(code)) {
      warnings.push('Currency symbols detected in state notes');
    }

    return isValid;
  }

  /**
   * Minimal whitespace normalization (preserve structure)
   * CRITICAL: Must maintain proper line breaks for Mermaid grammar
   */
  private normalizeWhitespace(code: string): string {
    return `${code
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // CRITICAL: Do NOT remove trailing whitespace aggressively - it breaks note syntax
      .replace(/[ \t]+$/gm, '') // Only remove trailing spaces/tabs
      // CRITICAL: Ensure each 'note' statement is on its own line
      .replace(/(\s+)(note\s+(?:right|left|over)\s+of\s+\w+:)/g, '\n$2')
      // Ensure proper spacing around state transitions
      .replace(/(\w+\s*-->\s*\w+)/g, '\n$1')
      // Clean up excessive newlines but preserve necessary ones
      .replace(/\n{3,}/g, '\n\n')
      .replace(/^\n+/, '')
      .replace(/\n+$/, '')}\n`;
  }

  /**
   * Unicode code point validation
   */
  private isValidCodePoint(code: number): boolean {
    return (
      !Number.isNaN(code) && code >= 0 && code <= 0x10ffff && code !== 0xfffe && code !== 0xffff
    );
  }
}
